// ─────────────────────────── src/pages/AllBlogs.jsx ───────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import "./AllBlogs.css";

// Utility: safe get date string from Firestore Timestamp or JS Date
function formatDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
    return d ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  } catch {
    return "";
  }
}

// Utility: get first paragraph text from body array
function firstParagraphText(body) {
  if (!Array.isArray(body)) return "";
  const p = body.find((b) => b?.type === "paragraph" && typeof b?.html === "string");
  if (!p) return "";
  const text = p.html.replace(/<[^>]+>/g, " "); // strip tags
  return text.replace(/\s+/g, " ").trim();
}

export default function AllBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const q = query(
          collection(db, "blogs"),
          where("status", "in", ["published", "draft"]), // show both; change to ["published"] if needed
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBlogs(list);
      } catch (e) {
        console.error("Failed to load blogs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = useMemo(
    () =>
      blogs.map((b) => {
        const fallback = firstParagraphText(b.body);
        const excerpt = b.excerpt?.trim() || fallback;
        const short = excerpt.length > 160 ? excerpt.slice(0, 157) + "…" : excerpt;
        return { ...b, __excerpt: short, __date: formatDate(b.publishedAt || b.createdAt) };
      }),
    [blogs]
  );

  const openBlog = (b) => navigate(`/blogs/${b.slug || b.id}`);

  return (
    <div className="wb-blogs-page">
      {/* Hero / breadcrumb */}
      <section className="wb-hero">
        <div className="wb-hero-inner">
          <h1 className="wb-hero-title">Blogs</h1>
          <div className="wb-breadcrumbs">
            <span className="wb-crumb" onClick={() => navigate("/")}>Home</span>
            <span className="wb-crumb-sep">/</span>
            <span className="wb-crumb wb-crumb-active">Blogs</span>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="wb-grid-wrap">
        {loading ? (
          <div className="wb-skel-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="wb-card wb-skel" key={i}>
                <div className="wb-card-media wb-skel-block" />
                <div className="wb-card-body">
                  <div className="wb-skel-line" />
                  <div className="wb-skel-line wb-s" />
                  <div className="wb-skel-line wb-xs" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="wb-grid">
            {items.map((b) => (
              <article className="wb-card" key={b.id} onClick={() => openBlog(b)}>
                <div className="wb-card-media">
                  <img
                    src={b.mainPhotoURL || "https://picsum.photos/seed/wbblogs/1200/630"}
                    alt={b.title || "Blog image"}
                    loading="lazy"
                  />
                  {b.category ? <span className="wb-badge">{b.category}</span> : null}
                </div>
                <div className="wb-card-body">
                  <h3 className="wb-card-title" title={b.title}>{b.title || "Untitled"}</h3>
                  <p className="wb-card-excerpt">{b.__excerpt}</p>
                  <div className="wb-card-meta">
                    <span className="wb-meta-date">{b.__date}</span>
                    <button className="wb-readmore" type="button">READ MORE</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
// ───────────────────────────── End of AllBlogs.jsx ─────────────────────────────
